/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createSlice, AnyAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { InvalidReferences } from '../../../../ts/action';
import { CollectionsSlice } from '../../../../ts/state';
import { SET_ASSISTANT_ID } from '../../../ia/store/session/actionTypes';
import { DEFAULT_ACTIONS } from '../../util/actionUtil';
import {
  createCollection,
  updateCollection,
  fetchCollection,
  deleteCollection,
  listCollections,
  setCurrentCollectionId
} from './actions';
import { collectionsAdapter } from './selectors';

const initialState: CollectionsSlice = {
  ids: [],
  entities: {},
  listError: {},
  listFetched: null,
  listStatus: ApiStatus.IDLE,
  currentLoadingStatus: ApiStatus.IDLE,
  currentCollectionId: '',
  currentError: {},
  actions: [],
  availableFilters: {
    extensions: [],
    subActions: [],
    triggersActions: [],
    variables: [],
    actionsWithError: [],
    displayNames: []
  },
  selectedFilters: {
    actions: [],
    extension: [],
    triggers: [],
    variable: [],
    actionsWithError: [],
    displayNames: []
  },
  filtersApplied: false,
  filteredData: [],
  filteredWord: ''
};

export const computeState = (updates?: any): any => ({
  ..._.cloneDeep(initialState),
  ...updates
});

const checkIfMultipleNames = (arrayObject, title) => {
  const multipleOccurances =
    arrayObject.filter(o => o.title === title).length > 1;
  return multipleOccurances;
};

const invalidRefCount = (valueObj: InvalidReferences = {}) => {
  let invalid = 0;
  // exclude intents, entities, jump_to_handler
  const obj = { ...valueObj };
  if (obj?.entity) {
    delete obj?.entity;
  }
  if (obj?.intent) {
    delete obj.intent;
  }
  if (obj.jump_to_handler) {
    delete obj.jump_to_handler;
  }
  const values = Object.values(obj);
  for (const val of values) {
    invalid += val;
  }
  return invalid;
};

//returns action ID's for the filtered actions
const getFilteredActions = (availableFilters, selectedFilters): String[] => {
  const {
    extensions,
    subActions,
    triggersActions,
    variables,
    actionsWithError,
    displayNames
  } = availableFilters;
  const {
    triggers: selectedTriggers,
    actions: selectedActions,
    extension: selectedExtension,
    variable: selectedVariable,
    actionsWithError: selectedActionsWithError,
    displayNames: selectedDisplayNames
  } = selectedFilters;
  const callingActions = []; //keep track of actions that applied filter matches
  let numOfFiltersApplied = 0; //keep track of number of filters applied (max 5)

  if (selectedTriggers.length !== 0) {
    // any action that triggers another action
    if (selectedTriggers[0].id === 'actions-any') {
      triggersActions.forEach(subaction => {
        subaction.callingActions.forEach(action => {
          if (!callingActions.includes(action.action)) {
            callingActions.push(action.action);
          }
        });
      });
    } else {
      const selectedFilter = triggersActions.find(
        actions => actions.id === selectedTriggers[0].id
      );
      selectedFilter?.callingActions?.forEach(action => {
        callingActions.push(action.action);
      });
    }
    numOfFiltersApplied++;
  }
  if (selectedExtension.length !== 0) {
    //add action that extension calls out. There can be more than 1 action that uses same extension.
    if (selectedExtension[0].id === 'extensions-any') {
      extensions.forEach(extension => {
        extension.callingActions.forEach(action => {
          if (!callingActions.includes(action.action)) {
            callingActions.push(action.action);
          }
        });
      });
    } else {
      const selectedFilter = extensions.find(
        actions => actions.id === selectedExtension[0].id
      );
      selectedFilter.callingActions.forEach(action => {
        callingActions.push(action.action);
      });
    }
    numOfFiltersApplied++;
  }
  if (selectedVariable.length !== 0) {
    //add action that variable calls out. There can be more than 1 action that uses same variable.
    const selectedFilter = variables.find(
      actions => actions.id === selectedVariable[0].id
    );
    selectedFilter.callingActions.forEach(action => {
      callingActions.push(action.action);
    });
    numOfFiltersApplied++;
  }
  if (selectedActions.length !== 0) {
    subActions.forEach(action => callingActions.push(action.action));
    numOfFiltersApplied++;
  }

  if (selectedActionsWithError.length !== 0) {
    actionsWithError.forEach(action => callingActions.push(action.action));
    numOfFiltersApplied++;
  }

  if (selectedDisplayNames.length !== 0) {
    displayNames.forEach(action => callingActions.push(action.action));
    numOfFiltersApplied++;
  }

  const actionIDs = callingActions;
  if (numOfFiltersApplied === 1) {
    return actionIDs;
  } else {
    const actionMap = new Map(); //keep track of number of occurences of each action.
    callingActions.forEach(action => {
      if (actionMap.has(action)) {
        actionMap.set(action, actionMap.get(action) + 1);
      } else {
        actionMap.set(action, 1);
      }
    });
    const actions = []; //occurences of action ids equal to num of filters applied
    for (const [k, v] of actionMap) {
      if (v === numOfFiltersApplied) {
        actions.push(k);
      }
    }
    return actions;
  }
};

//returns actions that match the filteredActionsIds
//if filter is applied in a collection, get the actions that are ONLY in that collection
const getFilteredRows = (
  allActions,
  collections,
  filteredActionsIDs,
  isCollection
) => {
  const actionsInCollections = [];
  let nonCollectionActions = [];
  collections.map(collection => {
    collection.action_references.forEach(action => {
      if (filteredActionsIDs.includes(action.action)) {
        actionsInCollections.push({
          ...action,
          id: action.action,
          examples: action.intents?.[0]?.examples_count,
          counts: action?.counts?.steps || action?.variables?.length
        });
      }
    });
  });
  nonCollectionActions = allActions.filter(
    row =>
      !Object.values(DEFAULT_ACTIONS).includes(row.action) &&
      filteredActionsIDs.includes(row.action)
  );

  const actionsWithId = nonCollectionActions.map(action => ({
    ...action,
    id: action.action,
    examples:
      action?.intents?.length > 0 ? action.intents[0].examples_count : 0,
    counts: action?.counts?.steps || action?.variables?.length
  }));

  const filteredRows = [...actionsInCollections];
  if (!isCollection) {
    filteredRows.push(...actionsWithId);
  }
  return filteredRows;
};

const collectionsSlice = createSlice({
  name: 'collections',
  initialState: collectionsAdapter.getInitialState(initialState),
  reducers: {
    applyFilters: (state, action) => {
      const { collectionsActions, isCollection } = action.payload;
      const actions = _.cloneDeep(state.actions);
      const availableFilters = _.cloneDeep(state.availableFilters);
      const selectedFilters = _.cloneDeep(state.selectedFilters);
      const filteredActionsIDS = getFilteredActions(
        availableFilters,
        selectedFilters
      );
      const filteredRows = getFilteredRows(
        actions,
        collectionsActions,
        filteredActionsIDS,
        isCollection
      );
      state.filteredData = filteredRows;
      if (
        !_.isEmpty(state.selectedFilters.actions) ||
        !_.isEmpty(state.selectedFilters.extension) ||
        !_.isEmpty(state.selectedFilters.triggers) ||
        !_.isEmpty(state.selectedFilters.variable) ||
        !_.isEmpty(state.selectedFilters.actionsWithError) ||
        !_.isEmpty(state.selectedFilters.displayNames)
      ) {
        //if at least one filter is selected set to true
        state.filtersApplied = true;
      } else {
        // if no filters are selected set to false
        state.filtersApplied = false;
      }
    },
    applySearchTerm: (state, action) => {
      state.filteredWord = action.payload;
    },
    clearAllFilters: (state, action) => {
      const { includingSearch } = action.payload;
      state.selectedFilters = {
        actions: [],
        extension: [],
        triggers: [],
        variable: [],
        actionsWithError: [],
        displayNames: []
      };
      if (includingSearch) {
        state.filteredWord = '';
      }
    },
    selectedFilters: (state, action) => {
      const { type, filter, clearFilter } = action.payload;
      //if clearFilter is true, set specific filter to empty
      switch (type) {
        case 'triggers':
          state.selectedFilters.triggers = clearFilter
            ? []
            : [
                {
                  id: filter.id,
                  label: filter.label
                }
              ];
          break;
        case 'extension':
          state.selectedFilters.extension = clearFilter
            ? []
            : [
                {
                  id: filter.id,
                  label: filter.label
                }
              ];
          break;
        case 'variable':
          state.selectedFilters.variable = clearFilter
            ? []
            : [
                {
                  id: filter.id,
                  label: filter.label
                }
              ];
          break;
        case 'actions':
          state.selectedFilters.actions = clearFilter ? [] : filter;
          break;
        case 'actionsWithError':
          state.selectedFilters.actionsWithError = clearFilter ? [] : filter;
          break;
        case 'displayNames':
          state.selectedFilters.displayNames = clearFilter ? [] : filter;
          break;
        default:
          break;
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCollection.pending, state => {
        state.currentLoadingStatus = ApiStatus.LOADING;
      })
      .addCase(fetchCollection.fulfilled, (state, action) => {
        collectionsAdapter.upsertOne(state, action.payload);
        // This will assign the fetched collection as the current collection
        state.currentCollectionId = action.payload.collection;
        state.currentLoadingStatus = ApiStatus.SUCCEEDED;
      })
      .addCase(fetchCollection.rejected, (state, action) => {
        state.currentError = action.error;
        state.currentLoadingStatus = ApiStatus.ERROR;
      })
      .addCase(createCollection.pending, state => {
        state.currentLoadingStatus = ApiStatus.LOADING;
      })
      .addCase(createCollection.fulfilled, (state, action) => {
        collectionsAdapter.addOne(state, action.payload);
        state.currentLoadingStatus = ApiStatus.SUCCEEDED;
      })
      .addCase(createCollection.rejected, (state, action) => {
        state.currentError = action.error;
        state.currentLoadingStatus = ApiStatus.ERROR;
      })
      .addCase(setCurrentCollectionId.pending, state => {
        state.currentLoadingStatus = ApiStatus.LOADING;
      })
      .addCase(setCurrentCollectionId.fulfilled, (state, action) => {
        // This will assign the fetched collection as the current collection
        state.currentCollectionId = action.payload;
        state.currentLoadingStatus = ApiStatus.SUCCEEDED;
      })
      .addCase(setCurrentCollectionId.rejected, (state, action) => {
        state.currentError = action.error;
        state.currentLoadingStatus = ApiStatus.ERROR;
      })
      .addCase(updateCollection.pending, state => {
        state.currentLoadingStatus = ApiStatus.LOADING;
      })
      .addCase(updateCollection.fulfilled, (state, action) => {
        collectionsAdapter.setOne(state, action.payload);
        state.currentLoadingStatus = ApiStatus.SUCCEEDED;
      })
      .addCase(updateCollection.rejected, (state, action) => {
        state.currentError = action.error;
        state.currentLoadingStatus = ApiStatus.ERROR;
      })
      .addCase(deleteCollection.pending, state => {
        state.currentLoadingStatus = ApiStatus.LOADING;
      })
      .addCase(deleteCollection.fulfilled, state => {
        collectionsAdapter.removeOne(state, state.currentCollectionId); // action.payload is better
        state.currentLoadingStatus = ApiStatus.SUCCEEDED;
      })
      .addCase(deleteCollection.rejected, (state, action) => {
        state.currentError = action.error;
        state.currentLoadingStatus = ApiStatus.ERROR;
      })
      .addCase(listCollections.pending, state => {
        state.listStatus = ApiStatus.LOADING;
      })
      .addCase(listCollections.fulfilled, (state, action) => {
        const { collections, actions } = action.payload;
        // add status property for each collection and actions within the collection
        const modifiedActionsInCollections = collections.map(eachCollection => {
          const action_references = eachCollection.action_references.map(
            eachAction => {
              const hasDuplicatedActions = checkIfMultipleNames(
                actions,
                eachAction.title
              );
              const invalid = invalidRefCount(eachAction.invalid_references);
              const status =
                invalid > 0
                  ? 'invalid references'
                  : hasDuplicatedActions
                  ? 'duplicate action'
                  : '';
              return {
                ...eachAction,
                status
              };
            }
          );
          const hasMultipleOccurancs = checkIfMultipleNames(
            collections,
            eachCollection.title
          );
          return {
            ...eachCollection,
            status: hasMultipleOccurancs ? 'duplicate collection' : 'no errors',
            action_references
          };
        });
        collectionsAdapter.setAll(state, modifiedActionsInCollections);
        // add status propert for each action NOT in the collection
        const modifiedActions = actions.map(eachAction => {
          const hasDuplicatedActions = checkIfMultipleNames(
            actions,
            eachAction.title
          );
          const invalid = invalidRefCount(eachAction.invalid_references);
          const status =
            invalid > 0
              ? 'invalid references'
              : hasDuplicatedActions
              ? 'duplicate action'
              : 'no errors';
          return {
            ...eachAction,
            status
          };
        });
        state.actions = modifiedActions;
        const { subactions, extensions, variables } = action.payload.filters;
        const triggersActions = [];
        const extensionValues = [];
        const variableValues = [];
        const actionsWithErrorValues = [];
        const displayNameValues = [];
        subactions?.forEach(sub => {
          //check if calling action has only system actions
          const nonParentSystemAction = sub.calling_actions.find(
            parentAction =>
              !Object.values(DEFAULT_ACTIONS).includes(parentAction.action)
          );
          //if there is not a single user created action, do nothing
          if (!nonParentSystemAction) {
            return;
          }
          triggersActions.push({
            id: sub.action,
            label: sub.title,
            callingActions: sub.calling_actions
          });
        });
        extensions?.forEach(sub => {
          extensionValues.push({
            id: sub.catalog_item_id,
            label: sub.title,
            callingActions: sub.calling_actions
          });
        });
        variables?.forEach(sub => {
          const variableTitle = sub?.variable;
          variableValues.push({
            id: variableTitle,
            label: variableTitle,
            callingActions: sub.calling_actions
          });
        });

        // add actions with errors to the array
        modifiedActions.forEach(sub => {
          if (sub.status === 'invalid references') {
            actionsWithErrorValues.push({ action: sub.action });
          }
          if (sub.display_name) {
            displayNameValues.push({ action: sub.action });
          }
        });

        // add actions with errors from the collections to the array
        modifiedActionsInCollections.forEach(sub => {
          sub.action_references.forEach(action => {
            if (action.status === 'invalid references') {
              actionsWithErrorValues.push({ action: action.action });
            }
            if (action.display_name) {
              displayNameValues.push({ action: action.action });
            }
          });
        });

        state.availableFilters.triggersActions = triggersActions;
        state.availableFilters.extensions = extensionValues;
        state.availableFilters.variables = variableValues;
        state.availableFilters.subActions = subactions;
        state.availableFilters.actionsWithError = actionsWithErrorValues;
        state.availableFilters.displayNames = displayNameValues;
        state.listStatus = ApiStatus.SUCCEEDED;
      })
      .addCase(listCollections.rejected, (state, action) => {
        state.listError = action.error;
        state.listStatus = ApiStatus.ERROR;
      })
      .addCase(SET_ASSISTANT_ID, (_state, action: AnyAction) => {
        if (action.assistantId) {
          return collectionsAdapter.getInitialState(initialState);
        }
      });
  }
});

export const {
  applyFilters,
  applySearchTerm,
  clearAllFilters,
  selectedFilters
} = collectionsSlice.actions;
export const collectionsReducer = collectionsSlice.reducer;
